import React from 'react';
import { createRoot } from 'react-dom/client';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { IonReactRouter } from '@ionic/react-router';
import { ThemeProvider } from '@mui/material/styles';
import { Provider as HttpProvider } from 'use-http';
import { Provider as ReduxProvider } from 'react-redux';
import useTheme from 'hooks/useTheme';
import { AuthProvider } from './providers/auth-provider';
import { AppStateProvider } from './providers/app-state/app-state-provider';
import { FeatureFlagsProvider } from './providers/featureflags-provider';
import AnalyticsProvider from './providers/AnalyticsProvider';
import PostHogPageViewShell from './providers/PostHogPageViewShell';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { initializeSentry } from './sentry';
import PlatformView from './common/molecules/shells/platformView/PlatformView';
import { store } from './redux/store';
import AuthShell from './providers/AuthShell';
import IonAppShell from './providers/IonAppShell';
import HealthCheck from './providers/HealthCheck';
import httpConfig from './settings/httpConfig';
// Call the element loader after the platform has been bootstrapped
defineCustomElements(window);

const theme = useTheme();

initializeSentry();

const container = document.getElementById('root');
const root = createRoot(container);

// StrictMode is fail-open: ON by default, only off when explicitly disabled
// via `VITE_STRICT_MODE=false` in an `.env*` file. Unset / typo / any other
// value keeps it on. Production builds strip StrictMode regardless.
const strictModeDisabled = import.meta.env.VITE_STRICT_MODE === 'false';
const StrictWrap = strictModeDisabled ? React.Fragment : React.StrictMode;

root.render(
  <StrictWrap>
    <AnalyticsProvider>
      <ReduxProvider store={store}>
        <HttpProvider options={httpConfig} url={import.meta.env.VITE_API_BASEURL}>
          <IonReactRouter>
            <PostHogPageViewShell>
              <HealthCheck>
                <AuthProvider>
                  <ThemeProvider theme={theme}>
                    <FeatureFlagsProvider>
                      <AppStateProvider>
                        <PlatformView>
                          <IonAppShell>
                            <AuthShell>
                              <App />
                            </AuthShell>
                          </IonAppShell>
                        </PlatformView>
                      </AppStateProvider>
                    </FeatureFlagsProvider>
                  </ThemeProvider>
                </AuthProvider>
              </HealthCheck>
            </PostHogPageViewShell>
          </IonReactRouter>
        </HttpProvider>
      </ReduxProvider>
    </AnalyticsProvider>
  </StrictWrap>,
);

serviceWorkerRegistration.unregister();

reportWebVitals();
